<script setup lang="ts">
  import { X, ChevronRight, Loader2, AlertCircle, RotateCcw } from 'lucide-vue-next'

  interface Props {
    open?: boolean
    loading?: boolean
    error?: boolean | string
    title?: string
    description?: string
    primaryLabel?: string
    secondaryLabel?: string
    retryLabel?: string
    errorMessage?: string
  }

  withDefaults(defineProps<Props>(), {
    open: false,
    loading: false,
    error: false,
    title: '',
    description: '',
    primaryLabel: '',
    secondaryLabel: '',
    retryLabel: 'Retry',
    errorMessage: 'Something went wrong. Please try again.'
  })

  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'primary-click'): void
    (e: 'secondary-click'): void
    (e: 'retry'): void
  }>()
</script>

<template>
  <Transition name="widget">
    <div
      v-if="open"
      class="ai-widget"
    >
      <!-- Navbar -->
      <div class="ai-widget__navbar">
        <button
          class="ai-widget__close"
          aria-label="Close"
          @click="emit('close')"
        >
          <X :size="20" />
        </button>
      </div>

      <!-- Loading state -->
      <div
        v-if="loading"
        class="ai-widget__loading"
      >
        <Loader2
          :size="24"
          class="ai-widget__spinner"
        />
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="ai-widget__error"
      >
        <AlertCircle
          :size="32"
          class="ai-widget__error-icon"
        />
        <p class="ai-widget__error-message">
          {{ typeof error === 'string' ? error : errorMessage }}
        </p>
        <button
          class="ai-widget__btn ai-widget__btn--primary"
          @click="emit('retry')"
        >
          <RotateCcw :size="16" />
          {{ retryLabel }}
        </button>
      </div>

      <!-- Content -->
      <template v-else>
        <div class="ai-widget__content">
          <div class="ai-widget__text">
            <!-- Sparkle icon with gradient fill -->
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M26.6667 4.00004V9.33337M29.3334 6.66671H24.0001M5.33341 22.6667V25.3334M6.66675 24H4.00008M13.2493 20.6667C13.1303 20.2053 12.8898 19.7842 12.5528 19.4472C12.2159 19.1103 11.7948 18.8698 11.3333 18.7507L3.15333 16.6414C3.01378 16.6018 2.89095 16.5177 2.80348 16.402C2.71602 16.2862 2.6687 16.1451 2.6687 16C2.6687 15.855 2.71602 15.7139 2.80348 15.5981C2.89095 15.4824 3.01378 15.3983 3.15333 15.3587L11.3333 13.248C11.7946 13.1291 12.2156 12.8888 12.5525 12.5521C12.8895 12.2154 13.1301 11.7946 13.2493 11.3334L15.3587 3.15338C15.3979 3.01327 15.4818 2.88984 15.5978 2.80191C15.7137 2.71398 15.8552 2.66638 16.0007 2.66638C16.1462 2.66638 16.2877 2.71398 16.4036 2.80191C16.5195 2.88984 16.6035 3.01327 16.6427 3.15338L18.7507 11.3334C18.8697 11.7948 19.1102 12.2159 19.4472 12.5529C19.7841 12.8898 20.2052 13.1303 20.6667 13.2494L28.8467 15.3574C28.9873 15.3962 29.1114 15.4801 29.1998 15.5961C29.2882 15.7122 29.3361 15.8541 29.3361 16C29.3361 16.146 29.2882 16.2879 29.1998 16.4039C29.1114 16.52 28.9873 16.6039 28.8467 16.6427L20.6667 18.7507C20.2052 18.8698 19.7841 19.1103 19.4472 19.4472C19.1102 19.7842 18.8697 20.2053 18.7507 20.6667L16.6413 28.8467C16.6021 28.9868 16.5182 29.1103 16.4022 29.1982C16.2863 29.2861 16.1448 29.3337 15.9993 29.3337C15.8538 29.3337 15.7123 29.2861 15.5964 29.1982C15.4805 29.1103 15.3965 28.9868 15.3573 28.8467L13.2493 20.6667Z"
                stroke="url(#paint0_linear_1_7123)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_1_7123"
                  x1="2.6687"
                  y1="16"
                  x2="29.3361"
                  y2="16"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#EF469B" />
                  <stop
                    offset="1"
                    stop-color="#9333EA"
                  />
                </linearGradient>
              </defs>
            </svg>
            <h3 class="ai-widget__title">
              {{ title }}
            </h3>
            <p class="ai-widget__description">
              {{ description }}
            </p>
          </div>
        </div>

        <!-- CTA -->
        <div class="ai-widget__cta">
          <button
            v-if="primaryLabel"
            class="ai-widget__btn ai-widget__btn--primary"
            @click="emit('primary-click')"
          >
            {{ primaryLabel }}
            <ChevronRight :size="18" />
          </button>
          <button
            v-if="secondaryLabel"
            class="ai-widget__btn ai-widget__btn--secondary"
            @click="emit('secondary-click')"
          >
            {{ secondaryLabel }}
          </button>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
  .ai-widget {
    position: fixed;
    /* 24px (launcher bottom) + 64px (launcher height) + 12px (gap) */
    bottom: 100px;
    right: 24px;
    z-index: 999;
    display: flex;
    flex-direction: column;
    width: min(400px, calc(100vw - 48px));
    height: min(600px, calc(100vh - 124px));
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  /* Navbar */
  .ai-widget__navbar {
    display: flex;
    justify-content: flex-end;
    padding: 16px 16px 0 16px;
  }

  .ai-widget__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 6px;
    transition:
      color 0.15s ease,
      background-color 0.15s ease;
  }

  .ai-widget__close:hover {
    color: #4b5563;
    background-color: #f3f4f6;
  }

  /* Loading state */
  .ai-widget__loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-widget__spinner {
    color: #c084fc;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Error state */
  .ai-widget__error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
  }

  .ai-widget__error-icon {
    color: #ef4444;
  }

  .ai-widget__error-message {
    font-size: 14px;
    color: #6b7280;
    text-align: center;
    margin: 0;
  }

  .ai-widget__error .ai-widget__btn {
    width: auto;
    height: 40px;
    padding: 0 20px;
  }

  /* Content area — fills available space */
  .ai-widget__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    overflow-y: auto;
  }

  /* Text block */
  .ai-widget__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ai-widget__sparkle {
    flex-shrink: 0;
    margin-bottom: 4px;
  }

  .ai-widget__title {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: 0;
    max-width: 324px;
    margin: 0;
    background: linear-gradient(135deg, rgba(239, 70, 154, 1), rgba(147, 51, 234, 1));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .ai-widget__description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #1f2937;
    max-width: 368px;
    margin: 0;
  }

  /* CTA section — pinned to bottom */
  .ai-widget__cta {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 12px 16px;
  }

  .ai-widget__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 48px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition:
      background-color 0.15s ease,
      opacity 0.15s ease;
  }

  .ai-widget__btn--primary {
    background: #1f2937;
    color: #ffffff;
    border: none;
  }

  .ai-widget__btn--primary:hover {
    background: #111827;
  }

  .ai-widget__btn--secondary {
    background: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
  }

  .ai-widget__btn--secondary:hover {
    background: #f9fafb;
    color: #4b5563;
  }

  /* Transitions — open: 200ms ease-out, close: 150ms ease-in */
  .widget-enter-active {
    transition:
      opacity 0.2s ease-out,
      transform 0.2s ease-out;
  }

  .widget-leave-active {
    transition:
      opacity 0.15s ease-in,
      transform 0.15s ease-in;
  }

  .widget-enter-from,
  .widget-leave-to {
    opacity: 0;
    transform: translateY(16px);
  }
</style>
